// Ported from vlookup-dramatic/app/mentorlookup/page.tsx by scripts/revamp/port-design.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Image from "../../components/image";
import { ArrowUpRight, Check } from "../../lib/icons";
import { BarChart3, Paintbrush, Rocket, Sparkles, Zap } from "./local-icons";
import * as settingActions from "../../../actions/settingActions";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";
import { usePageMeta } from "../../hooks/use-page-meta";
import Link from "../../components/link";
import { ButtonLink } from "../../components/button-link";
import { useHashScrollOnMount } from "../../lib/section-nav";
import { Icon } from "../../components/icon";
import { mentorLookUp, mluCrossSell, mluCtas, mluFinalCta, mluSectionHeads, } from "../../lib/content";
import { MluLiveSitesCollage } from "./live-sites-collage";
import { MluFeatureShowcase } from "./feature-showcase";
/* ── Live data (same calls as the old src/Pages/Saas page) ─────────────────────
   - GET pages/saas   : the CMS returns ONE rich-text block that duplicates this
                        page's copy in the old Bootstrap layout. Only its
                        meta_title / meta_description are used (and only when the
                        CMS has real values — today both are just "Saas").
   - GET brand        : partner logos; the "Trusted by" row shows the logos whose
                        title matches the design's MentorLookUp client list.   */
const DEFAULT_META = {
  title: "MentorLookUp — Smart Mentoring Platform | vLookUp",
  description: mentorLookUp.desc,
};
const isRealMeta = (v) => v && v.trim() && v.trim().toLowerCase() !== "saas";

/** Prefer a live GET brand image when the title matches; otherwise the local mark. */
function trustedLogos(brand, items) {
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
  const STOP = ["of", "and", "the", "for"];
  return items.map((item) => {
    const name = item.name || item;
    const words = String(name).split(" ").filter((w) => !STOP.includes(w.toLowerCase()));
    const candidates = [norm(words[0]), norm(words.map((w) => w[0]).join("")), norm(name)];
    const fromApi = (brand || []).find((b) => b.image && candidates.includes(norm(b.title)));
    return { title: name, image: (fromApi && fromApi.image) || item.src };
  }).filter((b) => b.image);
}

function formatStatCount(count) {
  const n = Number(count);
  if (!Number.isFinite(n)) return String(count ?? "");
  return n.toLocaleString("en-US");
}



function isActiveStat(row) {
  return row && row.status !== false && row.status !== 0 && row.status !== "false";
}

/** GET get-stats rows for one case (category ESSEC | SIM | SID). */
function statsForCase(c, liveRows) {
  const key = String(c.statsKey || "").toUpperCase();
  const rows = (liveRows || []).filter((r) => isActiveStat(r) && String(r.category || "").toUpperCase() === key);
  if (!rows.length) return c.stats || [];
  return rows.map((r) => ({ n: formatStatCount(r.count), l: r.heading || "" }));
}

/** The live white-label platform for a client case (from heroSites), if any. */
function platformFor(org) {
  const o = org.toLowerCase();
  return mentorLookUp.heroSites.find((s) => {
    const n = s.name.toLowerCase();
    return (o.includes("essec") && n.includes("essec")) || (o.includes("singapore institute of management") && n.includes("sim")) || (o.includes("directors") && n.includes("sid"));
  });
}

export default function MentorLookUpPage() {
  const dispatch = useDispatch();
  const brand = useSelector((s) => s.brand) || [];
  const [meta, setMeta] = useState(DEFAULT_META);
  const [liveStats, setLiveStats] = useState(null);
  useEffect(() => {
    dispatch(settingActions.getBrandData());
    let alive = true;
    agent.get(API.SAAS)
      .then((res) => {
        const d = (res && res.data && res.data.data) || {};
        if (!alive) return;
        setMeta({
          title: isRealMeta(d.meta_title) ? d.meta_title : DEFAULT_META.title,
          description: isRealMeta(d.meta_description) ? d.meta_description : DEFAULT_META.description,
        });
      })
      .catch(() => { });
    agent.get(API.GET_STATS + '?domain=SIM,SID,ESSEC')
      .then((res) => {
        if (!alive) return;
        const rows = (res && res.data && res.data.data) || [];
        setLiveStats(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (alive) setLiveStats([]); });
    return () => { alive = false; };
  }, [dispatch]);
  usePageMeta(meta);
  useHashScrollOnMount();
  const logos = trustedLogos(brand, mentorLookUp.trustedBy);
  return (<div className="mlu-page overflow-x-clip">
    {/* ===================== HERO + ANIMATED DASHBOARD (light SaaS) ===================== */}
    <section className="relative overflow-x-clip pb-14 pt-10 md:pb-20 md:pt-14">
      {/* layered light backdrop: dot grid + soft brand glows */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 [background-image:radial-gradient(rgba(26,58,92,0.08)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" />
      <div aria-hidden className="pointer-events-none absolute -right-40 -top-40 -z-10 size-[560px] rounded-full bg-brand-blue/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -left-40 top-1/2 -z-10 size-[420px] rounded-full bg-brand-blue/15 blur-3xl" />

      <div className="wrap">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          {/* Left — copy */}
          <div>
            {/* product lockup + badge */}
            <div className="flex flex-wrap items-center gap-4">
              <Image src="/revamp/images/mentorlookup-logo.webp" alt="MentorLookUp — Enabling Relevant Connections" width={420} height={90} className="h-12 w-auto md:h-14" priority />
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-red/10 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-red">
                <Zap className="size-3.5" />
                {mentorLookUp.badge}
              </span>
            </div>

            <h1 className="mt-6 font-display text-5xl font-semibold leading-[0.95] tracking-tight text-heading sm:text-6xl xl:text-7xl">
              Empower Your Community with{" "}
              <em className="italic text-brand-red">Smart</em> Mentoring
              Technology
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              {mentorLookUp.desc}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <ButtonLink href={mluCtas.primary.href} className="group h-auto rounded-full bg-brand-red py-4 pl-7 pr-3 text-base font-bold text-white hover:bg-brand-red-dark">
                {mluCtas.primary.label}
                <span className="cta-arrow-chip ml-3 grid size-9 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                  <ArrowUpRight className="size-4" />
                </span>
              </ButtonLink>
              <ButtonLink href={mluCtas.secondary.href} variant="ghost" className="h-auto rounded-full border border-brand-navy/25 px-7 py-4 text-base font-bold text-brand-navy hover:bg-brand-navy hover:text-white">
                {mluCtas.secondary.label}
              </ButtonLink>
            </div>

            {/* product-informative capability points */}
            <ul className="mt-9 grid max-w-xl grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {[
                { icon: Sparkles, label: "Smart matching engine" },
                { icon: Paintbrush, label: "100% white-label — your brand" },
                { icon: BarChart3, label: "Real-time impact dashboards" },
                { icon: Rocket, label: "Live in ~2 weeks" },
              ].map(({ icon: Ic, label }) => (<li key={label} className="flex items-center gap-2.5 text-sm font-semibold text-foreground/80">
                <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-brand-navy/8 text-brand-navy">
                  <Ic className="size-4" />
                </span>
                {label}
              </li>))}
            </ul>

            {/* trusted by — quiet single row */}
            <div className="mt-9 border-t border-neutral-200 pt-5">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400">
                Trusted by
              </span>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-6 gap-y-2">
                {logos.map((b) => (
                  <img
                    key={b.title}
                    src={b.image}
                    alt={b.title}
                    title={b.title}
                    loading="lazy"
                    className="h-9 w-auto max-w-[140px] object-contain opacity-80 grayscale transition-all hover:opacity-100 hover:grayscale-0"
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right — live white-label programmes, each screen is a link */}
          <MluLiveSitesCollage />
        </div>
      </div>
    </section>

    {/* ===================== STICKY PRODUCT SUB-NAV ===================== */}
    <nav aria-label="MentorLookUp sections" className="sticky top-[76px] z-40 border-y border-neutral-200 bg-white/85 backdrop-blur-md">
      <div className="wrap flex items-center gap-1 overflow-x-auto py-2.5 text-sm">
        {[
          { label: "Who it's for", href: "#who" },
          { label: "Platform features", href: "#features" },
          { label: "Launch in 4 steps", href: "#how-it-works" },
          { label: "Why MentorLookUp", href: "#why" },
          { label: "Client stories", href: "#cases" },
        ].map((l) => (<Link key={l.href} href={l.href} className="shrink-0 rounded-full px-4 py-1.5 font-bold text-neutral-500 transition-colors hover:bg-brand-navy/5 hover:text-brand-navy">
          {l.label}
        </Link>))}
        <Link href={mluCtas.primary.href} className="ml-auto hidden shrink-0 rounded-full bg-brand-red px-4 py-1.5 font-bold text-white transition-colors hover:bg-brand-red-dark md:inline-block">
          Book a demo
        </Link>
      </div>
    </nav>

    {/* ===================== WHO IT'S FOR ===================== */}
    <section id="who" className="relative scroll-mt-32 py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={mluSectionHeads.whoFor.eyebrow} title={accent(mluSectionHeads.whoFor.heading, "your organisation")} sub={mluSectionHeads.whoFor.sub} />
        <div className="mt-10 grid min-w-0 gap-6 md:grid-cols-3">
          {mentorLookUp.whoFor.map((w) => (<article key={w.title} className="flex min-w-0 flex-col overflow-hidden rounded-[1.5rem] border border-brand-navy/10 bg-card shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-xl md:rounded-[2rem]">
            <span aria-hidden className="h-1 bg-brand-red" />
            <div className="flex-1 px-8 py-8">
              <Icon name={w.icon} className="size-13" />
              <h3 className="mt-4 font-display text-2xl font-semibold leading-tight text-heading">
                {w.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {w.sub}
              </p>
            </div>
            <div className="px-8 py-6">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-blue">
                Perfect for
              </p>
              <ul className="mt-3.5 flex flex-col gap-2.5">
                {w.perfectFor.map((li) => (<li key={li} className="flex items-start gap-2.5 text-sm font-medium text-foreground/80">
                  <Check className="mt-0.5 size-4 shrink-0 text-brand-red" />
                  {li}
                </li>))}
              </ul>
            </div>
          </article>))}
        </div>
      </div>
    </section>

    {/* ===================== CORE FEATURES (expandable showcase) ===================== */}
    <section id="features" className="scroll-mt-32 bg-cream-deep py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={mluSectionHeads.features.eyebrow} title={accent(mluSectionHeads.features.heading, "scale")} sub="Explore each capability – everything you need, built in." />
        <div className="mt-10">
          <MluFeatureShowcase />
        </div>
      </div>
    </section>

    {/* ===================== HOW IT WORKS ===================== */}
    <section id="how-it-works" className="scroll-mt-32 py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={mluSectionHeads.steps.eyebrow} title={accent(mluSectionHeads.steps.heading, "live")} sub={mluSectionHeads.steps.sub} />
        <div className="mt-10 grid min-w-0 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {mentorLookUp.steps.map((s) => (<div key={s.n} className="relative min-w-0 rounded-[1.5rem] border border-brand-navy/10 bg-card p-7 shadow-sm md:rounded-[1.75rem]">
            <h3 className="font-display text-2xl font-semibold text-heading">
              {s.title}
            </h3>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
              {s.desc}
            </p>
          </div>))}
        </div>
      </div>
    </section>

    {/* ===================== WHY + GLANCE ===================== */}
    <section id="why" className="relative scroll-mt-32 bg-cream-deep py-14 md:py-20">
      <div className="wrap relative z-10">
        <SectionHead eyebrow={mluSectionHeads.reasons.eyebrow} title={accent(mluSectionHeads.reasons.heading, "real programmes")} />
        <div className="mt-10 grid items-start gap-10 lg:grid-cols-2">
          {/* reasons */}
          <div className="flex flex-col gap-7">
            {mentorLookUp.reasons.map((r) => (<div key={r.n} className="flex items-start gap-5">
              <div>
                <h3 className="font-display text-xl font-semibold text-heading">
                  {r.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {r.desc}
                </p>
              </div>
            </div>))}
          </div>

          {/* glance */}
          <div className="min-w-0 rounded-[1.5rem] border border-brand-navy/10 bg-card p-8 shadow-sm md:rounded-[2rem]">
            <div className="text-sm font-bold uppercase tracking-[0.14em] text-brand-navy/50">
              {mluSectionHeads.glance.heading}
            </div>
            <dl className="mt-5 divide-y divide-brand-navy/10">
              {mentorLookUp.glance.map((g) => (<div key={g.l} className="flex items-center gap-5 py-3.5">
                <dt className="min-w-[84px] font-display text-3xl font-semibold text-brand-blue">
                  {g.n}
                </dt>
                <dd className="text-sm leading-snug text-muted-foreground">{g.l}</dd>
              </div>))}
            </dl>
          </div>
        </div>
      </div>
    </section>

    {/* ===================== CLIENT CASES ===================== */}
    <section id="cases" className="scroll-mt-32 py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={mluSectionHeads.cases.eyebrow} title={accent(mluSectionHeads.cases.heading, "across the world")} sub={mluSectionHeads.cases.sub} />
        {/* Icon sits in a chip so the org name has full width (QA: titles
              were clipping to “School” / “of Management”). Stats sit in a
              3-up grid with a leading + on every count. */}
        <div className="mt-10 grid min-w-0 items-stretch gap-6 md:grid-cols-2 xl:grid-cols-3">
          {mentorLookUp.cases.map((c) => {
            const stats = statsForCase(c, liveStats);
            return (<article key={c.org} className="flex min-w-0 flex-col rounded-[1.5rem] border border-brand-navy/10 bg-card p-6 shadow-sm md:rounded-[2rem] md:p-7">
              <div className="flex min-w-0 items-start gap-3">
                <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-blue/10">
                  <Icon name={c.icon} className="size-7" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold leading-snug text-heading md:text-xl">
                    {c.org}
                  </h3>
                  <p className="mt-1 text-sm font-semibold leading-snug text-muted-foreground">
                    {c.programme}
                  </p>
                </div>
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">
                {c.desc}
              </p>
              {stats.length > 0 && (<div className="mt-6 grid grid-cols-3 gap-2 border-t border-brand-navy/10 pt-5">
                {stats.map((s) => (<div key={s.l} className="min-w-0">
                  <div className="font-display text-2xl font-semibold tabular-nums tracking-tight text-brand-blue">
                    {s.n}
                  </div>
                  <div className="mt-1 text-xs font-bold uppercase leading-tight tracking-wide text-muted-foreground">
                    {s.l}
                  </div>
                </div>))}
              </div>)}
              {platformFor(c.org) && (<a href={platformFor(c.org).href} target="_blank" rel="noopener noreferrer" className="group/case mt-auto inline-flex items-center gap-1.5 self-start pt-6 text-sm font-bold text-brand-red transition-colors hover:text-brand-navy">
                {mluSectionHeads.cases.caseCta}
                <ArrowUpRight className="size-4 transition-transform group-hover/case:translate-x-0.5 group-hover/case:-translate-y-0.5" />
              </a>)}
            </article>);
          })}
        </div>
      </div>
    </section>

    {/* ===================== CROSSLINK ===================== */}
    {/* Same panel spec as the CONTACT CTA below and as the mirror-image
            cross-sell on /for-organisations: rounded-[2.5rem], px-8/md:px-16,
            blob, display heading, circular arrow affordance. */}
    <section className="py-8 md:py-12">
      <div className="wrap">
        <div className="relative min-w-0 overflow-hidden rounded-[1.5rem] border border-brand-navy/10 bg-cream-deep px-8 py-12 md:rounded-[2.5rem] md:px-16 md:py-14">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 size-[360px] rounded-full bg-brand-blue/15 blur-3xl" />
          {/* QA (31 Aug): button stacks below the copy until lg so it never
                crowds or clips the panel edge at tablet/mobile widths. */}
          <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <span className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
                <span className="h-px w-8 bg-current opacity-50" />
                {mluCrossSell.eyebrow}
              </span>
              <h2 className="mt-3 font-display text-3xl font-semibold text-heading md:text-4xl">
                {mluCrossSell.heading}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {mluCrossSell.sub}
              </p>
            </div>
            <ButtonLink href={mluCrossSell.cta.href} className="cta-arrow-btn group h-auto max-w-full rounded-full bg-brand-red py-4 pl-7 pr-3 text-base font-bold text-white hover:bg-brand-red-dark lg:shrink-0">
              {mluCrossSell.cta.label}
              <span className="cta-arrow-chip ml-3 grid size-9 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <ArrowUpRight className="size-4" />
              </span>
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>

    {/* ===================== CONTACT CTA ===================== */}
    <section id="contact" className="scroll-mt-24 py-8 md:py-12">
      <div className="wrap">
        {/* QA (31 Aug): left aligned, in line with every other section on the site. */}
        <div className="relative min-w-0 overflow-hidden rounded-[1.5rem] bg-cream-deep px-8 py-14 ring-1 ring-brand-navy/5 md:rounded-[2.5rem] md:px-16 md:py-16">
          <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 size-[340px] rounded-full bg-brand-blue/15 blur-3xl" />
          <div className="relative">
            <h2 className="max-w-3xl font-display text-4xl font-semibold leading-tight text-heading md:text-6xl">
              {accent(mluFinalCta.heading, "together.", "font-normal italic text-brand-blue")}
            </h2>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              {mluFinalCta.sub}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <ButtonLink href={mluFinalCta.primary.href} className="group h-auto rounded-full bg-brand-red py-4 pl-7 pr-3 text-base font-bold text-white hover:bg-brand-red-dark">
                {mluFinalCta.primary.label}
                <span className="cta-arrow-chip ml-3 grid size-9 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                  <ArrowUpRight className="size-4" />
                </span>
              </ButtonLink>
              <ButtonLink href={mluFinalCta.secondary.href} download target="_blank" rel="noopener noreferrer" variant="ghost" className="h-auto rounded-full border border-brand-navy/25 bg-card px-7 py-4 text-base font-bold text-brand-navy hover:bg-brand-navy hover:text-white">
                {mluFinalCta.secondary.label}
              </ButtonLink>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">
              No commitment required. 30-minute demo, tailored to your use case.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>);
}
/* ---------- local helpers ---------- */
/** Renders a plain content heading with the page's italic accent on `accentText`. */
function accent(heading, accentText, accentClass = "font-normal italic text-brand-red") {
  const idx = heading.lastIndexOf(accentText);
  if (idx === -1)
    return heading;
  return (<>
    {heading.slice(0, idx)}
    <em className={accentClass}>{heading.slice(idx, idx + accentText.length)}</em>
    {heading.slice(idx + accentText.length)}
  </>);
}
// QA (31 Aug): standard sitewide section head — left aligned, eyebrow with the
// rule line (the centered variant this page used was off-pattern).
function SectionHead({ eyebrow, title, sub, }) {
  return (<div className="max-w-3xl">
    <span className="mb-4 flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
      <span className="h-px w-8 bg-current opacity-50" />
      {eyebrow}
    </span>
    <h2 className="font-display text-4xl font-semibold leading-[1.02] text-heading md:text-5xl">
      {title}
    </h2>
    {sub && (<p className="mt-5 text-lg leading-relaxed text-muted-foreground">
      {sub}
    </p>)}
  </div>);
}
